import * as React from 'react';
import styled from '@emotion/styled';
import { useHighlightHandlers } from '../../contexts/HighlightContext/Highlight';
import { Typography as MuiTypography } from '@mui/material';
import CardContent from '@mui/material/CardContent';
import { CardProps } from './Card';
import { useTranslation } from 'next-i18next';
import { CustomButton } from '../CustomButton';
import { getText } from '../../utils/get-text';
import { useAppContext } from '../../contexts/AppContext';

export interface CardContentProps extends CardProps {
  title?: string | undefined;
  limit?: number | undefined;
  description?: string | undefined;
  hideDescription?: boolean | undefined;
  hidePlayButton?: boolean | undefined;
  className?: string;
}

const Typography = React.forwardRef<HTMLInputElement, any>((props, ref) =>
  useHighlightHandlers(MuiTypography, { forwardedRef: ref, ...props })
);

const CardContentStyled = styled(CardContent)(({ theme }) => ({
  width: '100%',
  position: 'relative',
  zIndex: 2,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  textAlign: 'left',
  padding: theme.spacing(2),
  height: '100%',
  border: theme.theme_component_card_content_border || 'none',
  borderTop: 'none',
  pointerEvents: 'none',
  flex: 1,
})) as typeof CardContent;

const CardContentInfo = styled('div')(({ theme }) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
}));

const CardTitle = styled(Typography)(({ theme }) => ({
  width: '100%',
  display: 'inline-block',
  color: '#000',
  [theme.breakpoints.between('cmd', 'clg')]: {
    fontSize: '9px',
    lineHeight: '40px'
  },
}));

const DeveloperName = styled(Typography)(({ theme }) => ({
  height: 'auto',
}));

const ButtonStyled = styled(CustomButton)(({ theme }) => ({
  display: 'inline-block',
  margin: 0,
  pointerEvents: 'auto',
  alignSelf: 'center',
  borderRadius: theme.theme_component_carousel_d_card_button_border_radius || '46px',
  background: theme.theme_component_carousel_d_card_button_background || theme.palette.primary.main,
  color: theme.theme_component_carousel_d_card_button_color || theme.palette.primary.contrastText,
  '&:hover': {
    background: theme.theme_component_carousel_d_card_button_hover_background_color,
    color: theme.theme_component_carousel_d_card_button_active_background_color
  },
}));

export const CollectionContentG = (props: CardContentProps) => {
  const { hideTitle, title, hideDeveloperName, company, limit, className, hidePlayButton } = props;

  const { t } = useTranslation('card_content_e');
  const buttonRef = React.useRef<HTMLButtonElement | null>(null);

  const { useConfig } = useAppContext();
  const { componentOptions } = useConfig();

  return (
    <>
      {(title || company) && (
        <CardContentStyled className={className}>
          <CardContentInfo>
            {!hideTitle && (
              <CardTitle variant="h3" noWrap>
                {getText(title || '', limit || 30)}
              </CardTitle>
            )}
            {!hideDeveloperName && (
              <DeveloperName variant="body2" color="textPrimary">
                {company && getText(company, limit || 35)}
              </DeveloperName>
            )}
          </CardContentInfo>
          {!hidePlayButton && (
            <ButtonStyled
              ref={buttonRef}
              variant="contained"
              disableElevation
              disableRipple
              disableFocusRipple
              size="small"
            >
              {componentOptions.component_card_title_text || t('play_now', 'Play')}
            </ButtonStyled>
          )}
        </CardContentStyled>
      )}
    </>
  );
};
